import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilePdf, MicrosoftExcelLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const defaultRange = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: first.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
};

export default function ContactStatement({ contact }) {
  const { isAdmin } = useAuth();
  const [range, setRange] = useState(defaultRange());
  const [downloading, setDownloading] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["statement", contact.id, range.start, range.end],
    queryFn: async () =>
      (
        await api.get(
          `/contacts/${contact.id}/statement?start=${range.start}&end=${range.end}`
        )
      ).data,
  });

  const download = async (ext) => {
    setDownloading(ext);
    try {
      const url = `/contacts/${contact.id}/statement.${ext}?start=${range.start}&end=${range.end}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type:
          ext === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ekstre-${contact.name}-${range.start}-${range.end}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("İndirme başarısız");
    } finally {
      setDownloading(null);
    }
  };

  const rows = data?.rows || [];
  const opening = data?.opening_balance || 0;
  const closing = data?.closing_balance || 0;

  const balColor = (v) => (v > 0 ? "text-emerald-700" : v < 0 ? "text-red-600" : "text-muted-foreground");

  const typeLabel = (r) => {
    if (r.kind === "invoice") {
      return r.direction === "outgoing" ? "Giden Fatura" : "Gelen Fatura";
    }
    return r.tx_kind === "income" ? "Tahsilat" : "Ödeme";
  };
  const typeClass = (r) => {
    if (r.kind === "invoice") {
      return r.direction === "outgoing"
        ? "bg-emerald-50 text-emerald-700"
        : "bg-indigo-50 text-indigo-700";
    }
    return r.tx_kind === "income" ? "bg-blue-50 text-blue-700" : "bg-rose-50 text-rose-700";
  };

  return (
    <div data-testid="contact-statement">
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-border bg-white p-3">
        <div>
          <Label>Baş. Tarihi</Label>
          <input
            type="date"
            value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })}
            data-testid="stmt-start"
            className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-sm"
          />
        </div>
        <div>
          <Label>Bit. Tarihi</Label>
          <input
            type="date"
            value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })}
            data-testid="stmt-end"
            className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-sm"
          />
        </div>
        {isAdmin && <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => download("pdf")}
            disabled={downloading === "pdf"}
            data-testid="stmt-download-pdf"
          >
            <FilePdf size={16} className="mr-1" />
            {downloading === "pdf" ? "Hazırlanıyor..." : "PDF İndir"}
          </Button>
          <Button
            onClick={() => download("xlsx")}
            disabled={downloading === "xlsx"}
            data-testid="stmt-download-xlsx"
          >
            <MicrosoftExcelLogo size={16} className="mr-1" />
            {downloading === "xlsx" ? "Hazırlanıyor..." : "Excel İndir"}
          </Button>
        </div>}
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-md border border-border bg-white p-4">
          <div className="overline">Açılış Bakiyesi</div>
          <div className={`mono mt-2 text-xl font-semibold ${balColor(opening)}`} data-testid="stmt-opening">
            {fmtMoney(opening)}
          </div>
        </div>
        <div className="rounded-md border border-border bg-white p-4">
          <div className="overline">Dönem İçi Hareket</div>
          <div className="mono mt-2 text-xl font-semibold">{rows.length}</div>
        </div>
        <div className="rounded-md border border-border bg-white p-4">
          <div className="overline">Kapanış Bakiyesi</div>
          <div className={`mono mt-2 text-2xl font-black ${balColor(closing)}`} data-testid="stmt-closing">
            {fmtMoney(closing)}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider">
              <th className="px-3 py-2">Tarih</th>
              <th className="px-3 py-2">Tip</th>
              <th className="px-3 py-2">Belge / Açıklama</th>
              <th className="px-3 py-2 text-right">Tutar</th>
              <th className="px-3 py-2 text-right">TL</th>
              <th className="px-3 py-2 text-right">Bakiye</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Yükleniyor...</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Bu tarih aralığında hareket yok.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.id}-${i}`} className="border-b border-border/60">
                <td className="px-3 py-2 mono">{fmtDate(r.date)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${typeClass(r)}`}>
                    {typeLabel(r)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {r.kind === "invoice" && r.number && (
                    <span className="mono mr-2 rounded-sm bg-secondary px-1.5 py-0.5 text-xs">{r.number}</span>
                  )}
                  <span className="text-muted-foreground">{r.description || "-"}</span>
                </td>
                <td className="px-3 py-2 mono text-right">{fmtMoney(r.amount, r.currency)}</td>
                <td className="px-3 py-2 mono text-right text-muted-foreground">{fmtMoney(r.amount_try)}</td>
                <td className={`px-3 py-2 mono text-right font-semibold ${balColor(r.running_balance)}`}>
                  {fmtMoney(r.running_balance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
